import React, { ButtonHTMLAttributes } from 'react'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { Modal } from '.'
import {
  DialogSize,
  DialogContainer,
  DialogHeader,
  DialogBody,
  DialogFooter,
  footerButtonCss
} from './dialog'
import { CsConfirmationIcon, CsWarningIcon, StyledIconProps } from '../icons'
import { drilldownHeaderCss } from '../text'
import { Button } from '@contrast-security-inc/design-system-buttons'

const titleContainerCss = css`
  display: flex;
  align-items: center;
`

const titleIconCss = css`
  flex-shrink: 0;
  color: ${theme.color.white};
  margin-right: 8px;
  font-size: 18px;
`

const headerContainerCss = css`
  background-color: ${theme.color.neutral100};
`

const closeIconCss = css`
  color: ${theme.color.neutral60};
`

export interface ConfirmationDialogProps {
  isOpen: boolean
  onCancel: () => void
  onConfirm: () => void
  renderTitle: () => React.ReactNode
  renderBody: () => React.ReactNode
  renderConfirmMessage: () => React.ReactNode
  renderCancelMessage: () => React.ReactNode
  size?: DialogSize
  confirmButtonProps?: ButtonHTMLAttributes<HTMLButtonElement>
  confirmButtonPendoId?: string
  children?: React.ReactNode
  className?: string
}

export function ConfirmationDialog(props: ConfirmationDialogProps) {
  return <GenericConfirmationDialog {...props} titleIcon={CsConfirmationIcon} />
}

export function ConfirmationWarningDialog(props: ConfirmationDialogProps) {
  return <GenericConfirmationDialog {...props} titleIcon={CsWarningIcon} />
}

interface GenericProps extends ConfirmationDialogProps {
  readonly titleIcon: React.FC<StyledIconProps>
}

function GenericConfirmationDialog({
  isOpen,
  onCancel,
  onConfirm,
  titleIcon: Icon,
  renderTitle,
  renderBody,
  renderConfirmMessage,
  renderCancelMessage,
  size = 'medium',
  confirmButtonProps = {},
  confirmButtonPendoId,
  className
}: GenericProps) {
  return (
    <Modal isOpen={isOpen} onRequestClose={onCancel} className={className}>
      <DialogContainer size={size}>
        <DialogHeader
          onCancel={onCancel}
          headerContainerClassName={headerContainerCss}
          closeIconClassName={closeIconCss}
        >
          <div className={titleContainerCss}>
            <Icon className={titleIconCss} size={17} />
            <div
              className={cx(
                drilldownHeaderCss,
                css`
                  color: ${theme.color.white};
                `
              )}
            >
              {renderTitle()}
            </div>
          </div>
        </DialogHeader>
        <DialogBody>{renderBody()}</DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            data-testid="modal-cancel-button"
            className={footerButtonCss}
            onClick={onCancel}
          >
            {renderCancelMessage()}
          </Button>
          <Button
            data-e2e="primary-button"
            data-testid="confirm-button"
            data-pendo={confirmButtonPendoId}
            className={footerButtonCss}
            onClick={onConfirm}
            autoFocus
            {...confirmButtonProps}
          >
            {renderConfirmMessage()}
          </Button>
        </DialogFooter>
      </DialogContainer>
    </Modal>
  )
}
